import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  People as PeopleIcon,
  Class as ClassIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  Dashboard as DashboardIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import StatCard from "../components/infographics/StatCard";
import { useAuth } from "../contexts/AuthContext";
import PageContentLayout from "../components/layout/PageContentLayout";
import apiService from "../services/apiService";

const TLDashboard = () => {
  const theme = useTheme();
  const { user } = useAuth();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const welcomeName = user?.name || user?.user_name || "Team Leader";
  const [loading, setLoading] = useState(true);
  const [teachingStats, setTeachingStats] = useState({
    total_students: 0,
    total_classes: 0,
    attendance_rate: 0,
    total_assignments: 0,
  });

  const fetchStats = useCallback(async () => {
    setLoading(true);
    const result = await apiService.getPlatformTeachingStats();
    if (result.success && result.data) {
      setTeachingStats(result.data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <PageContentLayout
      title="Team Leader Dashboard"
      subtitle={`Welcome back, ${welcomeName}! Platform-wide teaching overview.`}
      breadcrumbs={[
        {
          label: "Team Leader Dashboard",
          icon: <DashboardIcon sx={{ color: "#ffffff", fontSize: 18 }} />,
        },
      ]}
      showBack={false}
      actions={<Chip label="Team Leader" color="primary" sx={{ fontWeight: 600 }} />}
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants}>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Students"
                value={teachingStats.total_students}
                icon={<PeopleIcon />}
                color="primary"
                trend="Platform-wide"
                trendColor="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Classes"
                value={teachingStats.total_classes}
                icon={<ClassIcon />}
                color="secondary"
                trend="Platform-wide"
                trendColor="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Attendance Rate"
                value={teachingStats.attendance_rate}
                icon={<CheckCircleIcon />}
                color="success"
                trend="Platform-wide"
                trendColor="info"
                suffix="%"
                decimals={1}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Assignments"
                value={teachingStats.total_assignments}
                icon={<AssignmentIcon />}
                color="warning"
                trend="Active homework"
                trendColor="info"
              />
            </Grid>
          </Grid>
        </motion.div>

        {/* Team Overview */}
        <motion.div variants={itemVariants}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Team Performance Overview
                  </Typography>
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <TrendingUpIcon sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      Overall Team Performance
                    </Typography>
                    <Typography variant="body1" sx={{ color: "text.secondary" }}>
                      Track your team's progress and performance metrics
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Quick Actions
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {[
                      { label: "Mark Attendance", icon: <CheckCircleIcon />, color: "success" },
                      { label: "View Classes", icon: <ClassIcon />, color: "primary" },
                      { label: "Manage Students", icon: <PeopleIcon />, color: "secondary" },
                      { label: "Review Assignments", icon: <AssignmentIcon />, color: "warning" },
                    ].map((action, index) => (
                      <motion.div
                        key={index}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Card
                          sx={{
                            p: 2,
                            cursor: "pointer",
                            border: `1px solid ${theme.palette.divider}`,
                            "&:hover": {
                              borderColor: `${action.color}.main`,
                              bgcolor: `${action.color}.light` + "10",
                            },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 1,
                                bgcolor: `${action.color}.light`,
                                color: `${action.color}.main`,
                              }}
                            >
                              {action.icon}
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {action.label}
                            </Typography>
                          </Box>
                        </Card>
                      </motion.div>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </motion.div>
      </motion.div>
      )}
    </PageContentLayout>
  );
};

export default TLDashboard;

